/**
 * The few things from campaign canon the GM might need right now.
 *
 * The server ranks; this only decides when to ask. Three triggers, for three
 * different reasons:
 *
 * - `canon.updated` — the agent just wrote something. This event has been
 *   published and subscribed since the canon graph was built and had no
 *   consumer, so a fact the agent established mid-scene reached the database
 *   and stopped there.
 * - new speech — the ranker scores against the last seven minutes of the
 *   transcript, so what is relevant changes as people talk, with no write
 *   involved.
 * - a slow tick — mention scores decay with a 90-second half-life, so the
 *   ordering goes stale on its own even when nothing at all happens.
 *
 * The currently-shown ids go back with every request. Stickiness has to be
 * decided against what is actually on screen, and the screen is the only thing
 * that knows — a GM with the session open on a laptop and a tablet has two
 * different panels, and server-side memory of "the" panel would be wrong for
 * both.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { getGlance, type GlanceEntity } from '@/api/client';

/** Re-rank on this cadence even in silence: scores decay whether or not
 *  anything happens, so a panel left alone slowly stops being true. */
const DECAY_REFRESH_MS = 45_000;

/** Coalesce bursts. A checkpoint can write several canon pages at once, and a
 *  talkative table produces a turn every few seconds; neither should mean a
 *  request each. */
const COALESCE_MS = 1_200;

export interface GlanceState {
  entities: GlanceEntity[];
  /** Ask again now — call on canon.updated and on new transcript. */
  poke: () => void;
}

export function useGlance(sessionId: string | null, live: boolean): GlanceState {
  const [entities, setEntities] = useState<GlanceEntity[]>([]);
  // Held in a ref, not state: this feeds the *next* request and must not
  // itself cause a re-render or the fetch would loop.
  const showing = useRef<string[]>([]);
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchNow = useCallback(async () => {
    if (!sessionId) return;
    try {
      const got = await getGlance(sessionId, showing.current);
      showing.current = got.entities.map((e) => e.id);
      setEntities(got.entities);
    } catch {
      // Non-fatal by design. This panel is a convenience beside the
      // storyboard; failing to refresh it must never disturb the session view.
      // Leaving the previous cards up is better than blanking them — stale
      // canon is still true canon.
    }
  }, [sessionId]);

  const poke = useCallback(() => {
    if (pending.current) return;
    pending.current = setTimeout(() => {
      pending.current = null;
      void fetchNow();
    }, COALESCE_MS);
  }, [fetchNow]);

  useEffect(() => {
    // Reset on every session change, not only on going non-live. Carrying one
    // session's ids into another's request is harmless today — stabilize()
    // skips ids it cannot find — but it is asking the server to keep cards
    // from a different campaign in place.
    setEntities([]);
    showing.current = [];
    if (!sessionId || !live) return;
    void fetchNow();
    const timer = setInterval(() => void fetchNow(), DECAY_REFRESH_MS);
    return () => {
      clearInterval(timer);
      if (pending.current) {
        clearTimeout(pending.current);
        pending.current = null;
      }
    };
  }, [sessionId, live, fetchNow]);

  return { entities, poke };
}
